import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useAssetManagementStore = defineStore('assetManagement', () => {
  // ========== 기본 상태 ==========
  const isLoading = ref(false);
  const error = ref(null);
  const uploadedFiles = ref([]);

  // ========== 전체 분석 결과 데이터 ==========
  const analysisData = ref(null);

  // ========== Computed - 기본 정보 ==========
  const companyName = computed(() => analysisData.value?.company_name || '');
  const analysisDatetime = computed(() => analysisData.value?.analysis_datetime || '');

  // ========== Computed - Step 1: 영업보고서 정보 ==========
  const step1AssetReport = computed(() => analysisData.value?.step_1_asset_report || null);
  
  const assetReportInfo = computed(() => 
    step1AssetReport.value?.asset_report_info || []
  );

  const assetReportOutputPath = computed(() => 
    step1AssetReport.value?.asset_report_output_path || ''
  );

  // 최신 영업보고서 정보 (첫 번째 항목)
  const latestReport = computed(() => {
    const reports = assetReportInfo.value;
    return reports.length > 0 ? reports[0] : null;
  });

  const businessYear = computed(() => latestReport.value?.business_year || '');
  const financial = computed(() => latestReport.value?.financial || null);
  const shareholders = computed(() => latestReport.value?.shareholders || []);
  const personnel = computed(() => latestReport.value?.personnel || []);
  const sanctions = computed(() => latestReport.value?.sanctions || []);

  // ========== Computed - Step 2: 인력 변동 분석 ==========
  const step2PersonnelAnalysis = computed(() => 
    analysisData.value?.step_2_personnel_analysis || null
  );

  const personnelAnalysisOutputPath = computed(() => 
    step2PersonnelAnalysis.value?.output_path || ''
  );

  const personnelInfo = computed(() => 
    step2PersonnelAnalysis.value?.personnel_info || null
  );

  const personnelPeriod = computed(() => 
    personnelInfo.value?.personnel_period || ''
  );

  const personnelShareholders = computed(() => 
    personnelInfo.value?.personnel_shareholders || null
  );

  const shareholdersDetails = computed(() => 
    personnelInfo.value?.shareholders_details || []
  );

  const currentPersonnel = computed(() => 
    personnelInfo.value?.current_personnel || []
  );

  const leftPersonnel = computed(() => 
    personnelInfo.value?.left_personnel || []
  );

  const movementByPeriod = computed(() => 
    personnelInfo.value?.movement_by_period || []
  );

  const movementStatistics = computed(() => 
    personnelInfo.value?.movement_statistics || null
  );

  // ========== Computed - Step 3: 총괄 평가 ==========
  const step3TotalEvaluation = computed(() => 
    analysisData.value?.step_3_total_evaluation || null
  );

  const evaluationOutputPath = computed(() => 
    step3TotalEvaluation.value?.output_path || ''
  );

  const evaluationInfo = computed(() => 
    step3TotalEvaluation.value?.evaluation_info || null
  );

  // 평가 요약
  const evaluationSummary = computed(() => 
    evaluationInfo.value?.evaluation_summary || null
  );

  const totalScore = computed(() => 
    evaluationSummary.value?.total_score || 0
  );

  const overallScoreRationale = computed(() => 
    evaluationSummary.value?.overall_score_rationale || ''
  );

  // 평가 요소
  const evaluationFactors = computed(() => 
    evaluationInfo.value?.evaluation_factors || null
  );

  // 재무적 요소
  const financialFactors = computed(() => 
    evaluationFactors.value?.financial_factors || null
  );

  const financialOverallScore = computed(() => 
    financialFactors.value?.overall_score || 0
  );

  const financialScoreDescription = computed(() => 
    financialFactors.value?.score_description || ''
  );

  const financialComponents = computed(() => 
    financialFactors.value?.components || null
  );

  // 비재무적 요소
  const nonFinancialFactors = computed(() => 
    evaluationFactors.value?.non_financial_factors || null
  );

  const nonFinancialOverallScore = computed(() => 
    nonFinancialFactors.value?.overall_score || 0
  );

  const nonFinancialScoreDescription = computed(() => 
    nonFinancialFactors.value?.score_description || ''
  );

  const nonFinancialComponents = computed(() => 
    nonFinancialFactors.value?.components || null
  );

  const riskFactors = computed(() => 
    nonFinancialFactors.value?.risk_factors || ''
  );

  const strengths = computed(() => 
    nonFinancialFactors.value?.strengths || ''
  );

  const recommendations = computed(() => 
    nonFinancialFactors.value?.recommendations || ''
  );

  const overallComment = computed(() => 
    nonFinancialFactors.value?.overall_comment || ''
  );

  // ========== 메서드 ==========
  
  // 전체 분석 결과 설정
  const setAnalysisData = (data) => {
    analysisData.value = data;
  };

  // 로딩 상태 설정
  const setLoading = (loading) => {
    isLoading.value = loading;
  };

  // 에러 설정
  const setError = (err) => {
    error.value = err;
  };

  // 파일 관리
  const addFiles = (files) => {
    uploadedFiles.value = [...uploadedFiles.value, ...files];
  };

  const removeFile = (index) => {
    uploadedFiles.value.splice(index, 1);
  };

  const clearFiles = () => {
    uploadedFiles.value = [];
  };

  // 전체 초기화
  const reset = () => {
    analysisData.value = null;
    isLoading.value = false;
    error.value = null;
    uploadedFiles.value = [];
  };

  return {
    // ========== 상태 ==========
    isLoading,
    error,
    uploadedFiles,
    analysisData,

    // ========== 기본 정보 ==========
    companyName,
    analysisDatetime,

    // ========== Step 1: 영업보고서 정보 ==========
    step1AssetReport,
    assetReportInfo,
    assetReportOutputPath,
    latestReport,
    businessYear,
    financial,
    shareholders,
    personnel,
    sanctions,

    // ========== Step 2: 인력 변동 분석 ==========
    step2PersonnelAnalysis,
    personnelAnalysisOutputPath,
    personnelInfo,
    personnelPeriod,
    personnelShareholders,
    shareholdersDetails,
    currentPersonnel,
    leftPersonnel,
    movementByPeriod,
    movementStatistics,

    // ========== Step 3: 총괄 평가 ==========
    step3TotalEvaluation,
    evaluationOutputPath,
    evaluationInfo,
    evaluationSummary,
    totalScore,
    overallScoreRationale,
    evaluationFactors,
    
    // 재무적 요소
    financialFactors,
    financialOverallScore,
    financialScoreDescription,
    financialComponents,
    
    // 비재무적 요소
    nonFinancialFactors,
    nonFinancialOverallScore,
    nonFinancialScoreDescription,
    nonFinancialComponents,
    riskFactors,
    strengths,
    recommendations,
    overallComment,

    // ========== 메서드 ==========
    setAnalysisData,
    setLoading,
    setError,
    addFiles,
    removeFile,
    clearFiles,
    reset,
  };
});
